import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categories, articles } from '@/mocks/siteData';
import { siteConfig } from '@/mocks/siteData';
import Breadcrumb from '@/components/base/Breadcrumb';
import { WebPageSchema } from '@/components/base/SchemaOrg';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'features');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setCurrentPage(1);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles = articles.filter((a) => a.category === activeCategory);
  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const paginatedArticles = filteredArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const categoryTitle = currentCategory
    ? `${currentCategory.name} - ${siteConfig.name}`
    : '分类 - ' + siteConfig.name;

  return (
    <>
      <WebPageSchema
        pageTitle={categoryTitle}
        pageDescription={currentCategory?.description || siteConfig.description}
      />

      <div className="pt-24 pb-16 md:pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '分类' },
            ]}
          />

          <div className="mt-6">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-3">
              {currentCategory?.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base">
              {currentCategory?.description}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mt-8 mb-10">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Articles List */}
          {paginatedArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {paginatedArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group block rounded-xl border border-background-200/70 bg-background-50 overflow-hidden hover:border-primary-200 transition-all duration-300"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700"
                        >
                          {tag}
                        </span>
                      ))}
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 font-heading mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-100">
                      <span className="text-xs text-foreground-500">{article.author}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-article-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-10 h-10 rounded-lg flex items-center justify-center border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                    currentPage === i + 1
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'border border-background-200/70 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-10 h-10 rounded-lg flex items-center justify-center border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}